import React, { FC } from 'react';
import {
  GaugeWrapper,
  GaugeSvgContainer,
  GaugeInfo,
  GaugeCurrentValue,
  GaugeExpectedText,
} from './SpeedGauge.styles';

export interface SpeedGaugeProps {
  current: number;
  expected: number;
  unit: string;
}

const CX = 55;
const CY = 56;
const R = 46;
const STROKE_WIDTH = 8;
const ARC_LEN = Math.PI * R;

function gaugeArc(r: number): string {
  return `M ${CX - r} ${CY} A ${r} ${r} 0 0 1 ${CX + r} ${CY}`;
}

function getNeedleEnd(ratio: number, length: number) {
  const angle = Math.PI * (1 - ratio);
  return {
    x: CX + length * Math.cos(angle),
    y: CY - length * Math.sin(angle),
  };
}

function getGaugeColor(ratio: number): string {
  if (ratio >= 0.9) return 'var(--allie-color-success-main)';
  if (ratio >= 0.7) return 'var(--allie-color-warning-main)';
  return 'var(--allie-color-error-main)';
}

export const SpeedGauge: FC<SpeedGaugeProps> = ({ current, expected, unit }) => {
  const ratio = expected > 0 ? Math.min(current / expected, 1) : 0;
  const isStopped = current === 0;
  const color = isStopped ? 'var(--allie-color-neutral-30)' : getGaugeColor(ratio);
  const filledLength = ARC_LEN * ratio;
  const needleEnd = getNeedleEnd(isStopped ? 0 : ratio, R - 10);

  return (
    <GaugeWrapper>
      <GaugeSvgContainer>
        <svg width="110" height="62" viewBox="0 0 110 62">
          {/* Track */}
          <path
            d={gaugeArc(R)}
            fill="none"
            stroke="var(--allie-color-neutral-20)"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
          />

          {/* Fill */}
          {!isStopped && (
            <path
              d={gaugeArc(R)}
              fill="none"
              stroke={color}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={`${filledLength} ${ARC_LEN}`}
            />
          )}

          {/* Needle */}
          <line
            x1={CX}
            y1={CY}
            x2={needleEnd.x}
            y2={needleEnd.y}
            stroke="var(--allie-color-neutral-50)"
            strokeWidth={2}
            strokeLinecap="round"
          />

          {/* Center dot */}
          <circle
            cx={CX}
            cy={CY}
            r={4}
            fill="var(--allie-color-neutral-50)"
          />
        </svg>
      </GaugeSvgContainer>

      <GaugeInfo>
        <GaugeCurrentValue $stopped={isStopped} $color={color}>
          {isStopped ? '—' : current.toLocaleString('es-AR')}
        </GaugeCurrentValue>
        <GaugeExpectedText>
          Esperado {expected.toLocaleString('es-AR')} {unit}
        </GaugeExpectedText>
      </GaugeInfo>
    </GaugeWrapper>
  );
};
